/**
 * InputArea Component
 *
 * Chat input with tool selection, file upload, and send functionality.
 * Auto-expands textarea and handles keyboard shortcuts.
 *
 * Usage:
 *   <InputArea
 *     onSend={handleSend}
 *     loading={loading}
 *     conversationId={conversationId}
 *   />
 */

import React, { useState, useRef, useEffect } from 'react';
import { FileInputMenu } from '../media/FileInputMenu';
import { MediaAsset } from '@/hooks/useMediaUpload';
import { theme } from '@/styles/theme';

export interface MediaSelection {
  type: 'upload' | 'library';
  files?: File[];  // For type='upload'
  assets?: MediaAsset[];  // For type='library'
}

export interface InputAreaProps {
  /** Text to put in the box (e.g. a starter suggestion); `n` changes on every new fill. */
  draft?: { text: string; n: number };
  // selectedTools is always undefined (the tool picker was removed; nothing used it); kept for the positional signature.
  onSend: (text: string, files?: File[], selectedTools?: string[], libraryAssets?: MediaAsset[]) => void;
  loading: boolean;
  conversationId?: string;
}

export const InputArea: React.FC<InputAreaProps> = ({
  draft,
  onSend,
  loading,
  conversationId,
}) => {
  const [input, setInput] = useState('');
  useEffect(() => { if (draft?.n) setInput(draft.text); }, [draft?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const [files, setFiles] = useState<File[]>([]);
  const [libraryAssets, setLibraryAssets] = useState<MediaAsset[]>([]);
  const [urlAssets, setUrlAssets] = useState<Array<{ url: string; filePromise: Promise<File | null> }>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Focus input on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    if (!input.trim() && files.length === 0 && libraryAssets.length === 0 && urlAssets.length === 0) return;
    if (loading) return;

    // Await URL fetches that started eagerly when URLs were added
    let urlFiles: File[] = [];
    if (urlAssets.length > 0) {
      const results = await Promise.allSettled(urlAssets.map((a) => a.filePromise));
      urlFiles = results
        .filter((r): r is PromiseFulfilledResult<File | null> => r.status === 'fulfilled' && r.value !== null)
        .map((r) => r.value as File);
    }

    onSend(input, [...files, ...urlFiles], undefined, libraryAssets);
    setInput('');
    setFiles([]);
    setLibraryAssets([]);
    setUrlAssets([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div
      className={`
        sticky bottom-0
        bg-card
        px-4 pb-4 pt-3
        transition-all duration-200
        ${isDragging ? 'border-primary bg-primary/5' : ''}
      `}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="w-full">
        {/* Media Previews - Small thumbnails above input */}
        {(files.length > 0 || libraryAssets.length > 0 || urlAssets.length > 0) && (
          <div className="mb-3 flex flex-wrap gap-3">
            {/* Uploaded Files */}
            {files.map((file, index) => {
              const isImage = file.type.startsWith('image/');
              const isVideo = file.type.startsWith('video/');
              const previewUrl = isImage || isVideo ? URL.createObjectURL(file) : null;

              return (
                <div
                  key={`file-${index}`}
                  className="relative group"
                >
                  {/* Thumbnail Container */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-inset dark:bg-card-darkAlt border-2 border-work/40">
                    {isImage && previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : isVideo && previewUrl ? (
                      <video
                        src={previewUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-ink-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Remove Button - X on top right (touch-friendly) */}
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute -top-3 -right-3 w-8 h-8 bg-bad hover:bg-bad text-white rounded-full flex items-center justify-center shadow-lg transition-all touch-manipulation"
                    aria-label={`Remove ${file.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Upload Badge */}
                  <div className="absolute top-1 left-1 bg-primary text-white text-[10px] px-1.5 py-0.5 rounded">
                    Upload
                  </div>

                  {/* Filename tooltip on hover */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
                    {file.name}
                  </div>
                </div>
              );
            })}

            {/* URL Assets */}
            {urlAssets.map((asset, index) => (
              <div key={`url-${index}`} className="relative group">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-inset dark:bg-card-darkAlt border-2 border-emerald-300">
                  <img src={asset.url} alt="URL" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
                <button
                  onClick={() => setUrlAssets(prev => prev.filter((_, i) => i !== index))}
                  className="absolute -top-3 -right-3 w-8 h-8 bg-bad hover:bg-bad text-white rounded-full flex items-center justify-center shadow-lg transition-all touch-manipulation"
                  aria-label="Remove URL"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="absolute top-1 left-1 bg-ok text-white text-[10px] px-1.5 py-0.5 rounded">URL</div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
                  {asset.url.split('/').pop()?.split('?')[0] || asset.url}
                </div>
              </div>
            ))}

            {/* Library Assets */}
            {libraryAssets.map((asset, index) => {
              const isImage = asset.media_type === 'image';
              const isVideo = asset.media_type === 'video';

              return (
                <div
                  key={`asset-${asset.id}`}
                  className="relative group"
                >
                  {/* Thumbnail Container */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-inset dark:bg-card-darkAlt border-2 border-purple-300">
                    {isImage ? (
                      <img
                        src={asset.public_url}
                        alt={asset.original_filename || asset.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : isVideo ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-ink-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-ink-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Remove Button - X on top right (touch-friendly) */}
                  <button
                    onClick={() => setLibraryAssets(prev => prev.filter((_, i) => i !== index))}
                    className="absolute -top-3 -right-3 w-8 h-8 bg-bad hover:bg-bad text-white rounded-full flex items-center justify-center shadow-lg transition-all touch-manipulation"
                    aria-label={`Remove ${asset.original_filename || asset.filename}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Library Badge */}
                  <div className="absolute top-1 left-1 bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                    Library
                  </div>

                  {/* Filename tooltip on hover */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
                    {asset.original_filename || asset.filename}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Input Container */}
        <div className="flex items-center gap-2 rounded-[10px] bg-card p-2 shadow-[inset_0_0_0_1px_rgb(var(--c-line-2))] transition-shadow focus-within:shadow-[inset_0_0_0_1px_rgb(var(--c-ink-2))]">
          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={conversationId ? "Ask for a change, or drop a photo" : "Describe it, or drop a photo…"}
              disabled={loading}
              className={`
                w-full rounded-btn
                border-0
                bg-transparent
                text-ink
                text-[15px]
                placeholder:text-ink-3
                px-2 py-3 pr-16
                focus:outline-none
                disabled:opacity-50 disabled:cursor-not-allowed
                resize-none
                transition-all duration-200
              `}
              rows={1}
              style={{ minHeight: '52px', maxHeight: '200px', fontSize: '16px' }}
            />

            {/* File Upload & Attachments */}
            <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
              {/* FileInputMenu - Upload or Library */}
              <FileInputMenu
                compact={true}
                mediaType="all"
                multiSelect={true}
                onFilesSelected={(files: File[]) => setFiles(prev => [...prev, ...files])}
                onLibrarySelected={(assets: MediaAsset[]) => setLibraryAssets(prev => [...prev, ...assets])}
                onUrlSelected={(url: string) => {
                  const filePromise = fetch(url)
                    .then((res) => res.blob())
                    .then((blob) => {
                      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
                      const name = `url_image_${Date.now()}.${ext}`;
                      return new File([blob], name, { type: blob.type || 'image/jpeg' });
                    })
                    .catch(() => null);
                  setUrlAssets(prev => [...prev, { url, filePromise }]);
                }}
                onError={(error) => console.error('File selection error:', error)}
                className="relative"
              />

              {/* Character Count */}
              {input.length > 0 && (
                <span className="text-xs text-ink-3 select-none">
                  {input.length}
                </span>
              )}
            </div>
          </div>

          {/* Send button: "Create" starts a new campaign, "Send" continues one */}
          <button
            onClick={handleSubmit}
            disabled={loading || (!input.trim() && files.length === 0)}
            className={`
              px-4 shrink-0
              bg-navy text-on-navy
              rounded-lg
              hover:bg-navy-3
              font-display text-sm font-bold
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
              flex items-center justify-center
              touch-manipulation
              h-11 min-w-[44px]
              ${loading ? 'animate-pulse' : ''}
            `}
            aria-label={conversationId ? 'Send message' : 'Create the campaign'}
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <span>{conversationId ? 'Send' : 'Create'}</span>
            )}
          </button>
        </div>
        {conversationId && <p className="m-0 px-1 pt-2 text-xs text-ink-3">Edits you make in the stages always win over chat.</p>}

      </div>

      {/* Drag & Drop Overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-primary/10 border-4 border-dashed border-primary rounded-lg flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-lg font-semibold text-primary">Drop files here</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputArea;
